import { useState } from 'react';
import { toast } from 'sonner';
import { SettingsCard } from '@/components/account/settings-card';
import { useThemeMode } from '@/modules/theme';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { queueMemberPreferencesWrite } from '@/infrastructure/adapters/memberPreferencesCache';
import { OrgInstance, OrgMember } from '@/types/orgTypes';

interface AccountSettingsTabProps {
  userId: string;
  orgMember: OrgMember | null;
  orgInstance: OrgInstance | null;
  membershipKnown: boolean;
  updateMember: (change: (member: OrgMember) => OrgMember) => void;
  updateInstance: (change: (instance: OrgInstance) => OrgInstance) => void;
  onOpenCookiePreferences: () => void;
  onSignOut: () => void;
}

export function AccountSettingsTab({ userId, orgMember, orgInstance, membershipKnown, updateMember, updateInstance, onOpenCookiePreferences, onSignOut }: AccountSettingsTabProps) {
  const services = useServices();
  const [busy, setBusy] = useState<'marketing' | 'watermark' | null>(null);
  const { mode: themeMode, setMode: setThemeMode } = useThemeMode();
  const [immersalToken, setImmersalToken] = useState('');
  const [immersalSaving, setImmersalSaving] = useState(false);
  const [immersalEditing, setImmersalEditing] = useState(false);

  const marketingConsent = orgMember?.memberPreferences.marketingConsent === true;
  const watermark = orgMember?.displayImmersalWatermark === true;
  const togglesOff = !membershipKnown || !orgMember || busy !== null;

  const toggleMarketing = async () => {
    if (togglesOff) return;
    const next = !marketingConsent;
    setBusy('marketing');
    try {
      await queueMemberPreferencesWrite(userId, () => services.auth.updateMarketingConsent({ marketingConsent: next }));
      updateMember(member => ({
        ...member,
        memberPreferences: { ...member.memberPreferences, marketingConsent: next, emailNotifications: next },
      }));
    } catch {
      toast.error('Failed to update marketing preferences');
    } finally {
      setBusy(null);
    }
  };

  const toggleWatermark = async () => {
    if (togglesOff) return;
    const next = !watermark;
    setBusy('watermark');
    try {
      await services.organization.updateOrgMemberImmersalWatermark(userId, next);
      updateMember(member => ({ ...member, displayImmersalWatermark: next }));
    } catch {
      toast.error('Failed to update watermark in AR experiences');
    } finally {
      setBusy(null);
    }
  };

  const saveImmersalToken = async (token: string) => {
    if (!membershipKnown) return;
    try {
      setImmersalSaving(true);
      await services.organization.updateOrgInstanceImmersalToken(userId, token);
      updateInstance(instance => ({ ...instance, orgInstanceImmersalConfigured: token !== '' }));
      setImmersalToken('');
      setImmersalEditing(false);
      toast.success(token ? 'Immersal developer token saved' : 'Immersal developer token removed');
    } catch {
      toast.error(token ? 'Failed to save Immersal developer token' : 'Failed to remove Immersal developer token');
    } finally {
      setImmersalSaving(false);
    }
  };

  return (
    <SettingsCard
      appearance={themeMode}
      onAppearanceChange={setThemeMode}
      marketingConsent={marketingConsent}
      onToggleMarketing={toggleMarketing}
      watermark={watermark}
      onToggleWatermark={toggleWatermark}
      immersalConfigured={!!orgInstance?.orgInstanceImmersalConfigured}
      immersalEditing={immersalEditing}
      immersalToken={immersalToken}
      immersalSaving={immersalSaving}
      onImmersalTokenChange={setImmersalToken}
      onImmersalReplace={() => { setImmersalEditing(true); setImmersalToken(''); }}
      onImmersalCancel={() => { setImmersalEditing(false); setImmersalToken(''); }}
      onImmersalSave={() => saveImmersalToken(immersalToken.trim())}
      onImmersalRemove={() => saveImmersalToken('')}
      onCookiePreferences={onOpenCookiePreferences}
      onSignOut={onSignOut}
      busy={busy}
      togglesDisabled={togglesOff}
      immersalDisabled={!membershipKnown}
    />
  );
}
